import {
  BarChart3,
  Brain,
  Clock,
  GraduationCap,
  HeartHandshake,
  LayoutDashboard,
  MessageCircle,
  Route,
  School,
  Target,
  BookOpenCheck,
  Zap,
} from "lucide-react";

const groups = [
  {
    icon: GraduationCap,
    title: "Voor de leerling",
    lead: "Meer zelfkennis, motivatie en vertrouwen.",
    metric: "Groeien",
    metricLabel: "in zelfkennis en eigenaarschap",
    items: [
      { label: "Meer zelfkennis", icon: Brain },
      { label: "Meer motivatie", icon: Zap },
      { label: "Betere studiekeuze", icon: Target },
    ],
  },
  {
    icon: HeartHandshake,
    title: "Voor de mentor",
    lead: "Meer overzicht en betere gesprekken.",
    metric: "Begeleiden",
    metricLabel: "met meer context en overzicht",
    items: [
      { label: "Meer overzicht", icon: LayoutDashboard },
      { label: "Betere gesprekken", icon: MessageCircle },
      { label: "Meer tijd", icon: Clock },
    ],
    highlight: true,
  },
  {
    icon: School,
    title: "Voor de decaan en school",
    lead: "Een professionele, doorlopende LOB-lijn.",
    metric: "Verbinden",
    metricLabel: "in één herkenbare LOB-aanpak",
    items: [
      { label: "Doorlopende LOB", icon: Route },
      { label: "Meer inzicht", icon: BarChart3 },
      { label: "Vastgelegde ontwikkeling", icon: BookOpenCheck },
    ],
  },
];

const ResultsSection = () => {
  return (
    <section className="relative py-24 lg:py-32 animate-on-scroll overflow-hidden">
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-primary/5 blur-[140px]" />

      <div className="container mx-auto max-w-6xl px-4 sm:px-6 relative">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <span className="inline-block text-[11px] font-bold tracking-[0.2em] uppercase text-primary mb-4 px-3 py-1 bg-accent rounded-full">
            Het resultaat
          </span>
          <h2 className="text-3xl lg:text-[2.8rem] font-extrabold text-secondary leading-tight [text-wrap:balance]">
            Wat het oplevert — voor iedereen.
          </h2>
          <p className="text-lg text-muted-foreground mt-4 leading-relaxed">
            Eén samenhangende aanpak, met een eigen waarde voor iedere rol.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {groups.map((g) => (
            <div
              key={g.title}
              className={`group relative rounded-3xl p-8 overflow-hidden transition-all hover:-translate-y-1.5 ${
                g.highlight
                  ? "bg-gradient-to-br from-primary via-primary to-[hsl(196,100%,38%)] text-primary-foreground shadow-large shadow-primary/25"
                  : "bg-card border border-border shadow-soft hover:shadow-large"
              }`}
            >
              <div
                className={`pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl ${
                  g.highlight
                    ? "bg-primary-foreground/20"
                    : "bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity"
                }`}
              />

              <div className="relative">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${
                    g.highlight ? "bg-primary-foreground/15" : "bg-primary/10"
                  }`}
                >
                  <g.icon
                    className={`w-6 h-6 ${
                      g.highlight ? "text-primary-foreground" : "text-primary"
                    }`}
                  />
                </div>

                {/* big metric */}
                <div
                  className={`text-4xl lg:text-5xl font-extrabold leading-none mb-2 ${
                    g.highlight ? "text-primary-foreground" : "text-primary"
                  }`}
                >
                  {g.metric}
                </div>
                <div
                  className={`text-sm font-semibold mb-6 ${
                    g.highlight
                      ? "text-primary-foreground/85"
                      : "text-muted-foreground"
                  }`}
                >
                  {g.metricLabel}
                </div>

                <h3
                  className={`text-lg font-bold mb-1 ${
                    g.highlight ? "text-primary-foreground" : "text-secondary"
                  }`}
                >
                  {g.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed mb-6 ${
                    g.highlight
                      ? "text-primary-foreground/80"
                      : "text-muted-foreground"
                  }`}
                >
                  {g.lead}
                </p>

                <ul
                  className={`space-y-3 pt-5 border-t ${
                    g.highlight
                      ? "border-primary-foreground/20"
                      : "border-border"
                  }`}
                >
                  {g.items.map((item) => (
                    <li
                      key={item.label}
                      className={`flex items-center gap-3 text-sm font-semibold ${
                        g.highlight
                          ? "text-primary-foreground"
                          : "text-secondary"
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${
                          g.highlight
                            ? "bg-primary-foreground/15 text-primary-foreground"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                      </span>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
